// LAYOUT-SPEC §0 — Beyond Scope (SAD-859), the PM's monthly pass
//
//   ONE QUESTION  What did our teams do that the award does not pay for, and
//                 which of it goes into a mod or REA?
//   PRIMARY OBJECT  The program's work beyond the award (Archetype B).
//   PRIMARY VERB    Set the scope (in bulk). Export is the hand-off.
//   WORST-CASE VOLUME  ~10 teams, a few hundred work products a year.
//
// Two kinds of work per team, never mixed:
//   - "Committed in a SOW, not in the award": a SOW's own deliverables
//     (origin sow), read-only here; each links to its deliverable;
//   - "Done, not in any document": the team's logged work products.
// The filters (scope, month) narrow the logged work; the team filter both.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ExecutionScopeAssessment as Scope } from '@mono/proto-clients';
import { SpecraButton, SpecraCheckbox, SpecraDashboardPage, SpecraEmptyState, SpecraFilterToolbar, SpecraHeading, SpecraLink, SpecraPageHeader, SpecraSectionHeader, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useResolveArtifactNames } from '../../../../hooks/useDeliveryDocuments';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { artifactClient } from '../../../../lib/connect/clients';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { GlossaryText } from '../../Glossary/GlossaryText';
import { CmTable } from '../components/common';
import { usePeopleDirectory, useTeamWriter } from '../components/TeamMembers';
import { useWorkProductColumns } from '../components/WorkProducts';
import { useAssessWorkProducts, useWorkProducts, useWorkstreamDetails } from '../hooks';
import { filterWorkProducts, formatCents, formatHours, monthLabel, monthOf, SCOPE_BUCKETS, scopeBucket, totals } from '../lib/workProducts';
import { buildOutOfScopeZip } from '../lib/workProductsExport';
import { useCmLookups } from '../lookups';
async function fetchArtifact(artifactId) {
    const dl = await artifactClient.generateDownloadUrl({
        artifactId
    });
    if (!dl.url) throw new Error('no download url');
    const url = new URL(dl.url, window.location.href);
    const href = url.pathname.startsWith('/storage/v1/artifacts/') ? `${url.pathname}${url.search}` : dl.url;
    const resp = await fetch(href);
    if (!resp.ok) throw new Error(resp.statusText);
    return resp.blob();
}
function saveBlob(blob, name) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 10000);
}
export function BeyondScopePage() {
    const { programId } = useProgramContext();
    const navigate = useNavigate();
    const lk = useCmLookups(programId);
    const writer = useTeamWriter(programId);
    // Every team's SOWs, in one ListWorkstreams read (SAD-994).
    const details = useWorkstreamDetails(programId);
    const q = useWorkProducts(programId);
    const dir = usePeopleDirectory();
    const assess = useAssessWorkProducts(programId, {
        success: (r)=>`Scope set on ${r.workProducts.length} work product${r.workProducts.length === 1 ? '' : 's'}`
    });
    const [scope, setScope] = useState('');
    const [month, setMonth] = useState('');
    const [team, setTeam] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [bulk, setBulk] = useState('out');
    const [exporting, setExporting] = useState(false);
    const all = q.data ?? [];
    const rows = filterWorkProducts(all, {
        scope,
        month,
        team
    });
    const months = useMemo(()=>[
            ...new Set(all.map((r)=>monthOf(r.producedOn)))
        ].sort().reverse(), [
        all
    ]);
    // Names are needed only by the export, so they are read when it runs.
    const resolveNames = useResolveArtifactNames();
    // A SOW's own deliverables, by the team whose SOW states them.
    const sowDutiesByTeam = useMemo(()=>{
        const teamOfSow = new Map();
        for (const d of details.data ?? []){
            for (const s of d.sows)teamOfSow.set(s.id, d.workstream?.id ?? '');
        }
        const m = new Map();
        for (const d of lk.duties){
            if (d.origin !== 'sow') continue;
            const t = teamOfSow.get(d.sowId);
            if (!t) continue;
            m.set(t, [
                ...m.get(t) ?? [],
                d
            ]);
        }
        for (const list of m.values())list.sort((a, b)=>a.title.localeCompare(b.title));
        return m;
    }, [
        details.data,
        lk.duties
    ]);
    const teams = lk.workstreams.filter((w)=>!team || w.id === team).map((w)=>({
            ws: w,
            sow: sowDutiesByTeam.get(w.id) ?? [],
            logged: rows.filter((r)=>r.workstreamId === w.id)
        })).filter((t)=>t.sow.length > 0 || t.logged.length > 0);
    const t = totals(rows);
    const outCount = rows.filter((r)=>scopeBucket(r.scopeAssessment) === 'out').length;
    const exportRows = filterWorkProducts(all, {
        scope: 'out',
        month,
        team
    });
    const toggle = (id, on)=>setSelected((prev)=>{
            const n = new Set(prev);
            if (on) n.add(id);
            else n.delete(id);
            return n;
        });
    const liveSelected = rows.filter((r)=>selected.has(r.id)).map((r)=>r.id);
    const selectColumn = writer.runsDelivery ? [
        {
            id: 'select',
            header: /*#__PURE__*/ _jsx("span", {
                className: "sr-only",
                children: "Select"
            }),
            width: 36,
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraCheckbox, {
                    "aria-label": `Select ${r.title}`,
                    checked: selected.has(r.id),
                    onClick: (e)=>e.stopPropagation(),
                    onCheckedChange: (c)=>toggle(r.id, c === true),
                    "data-testid": Testids.cmWorkProducts.select
                })
        }
    ] : [];
    const columns = useWorkProductColumns(programId, {
        lead: selectColumn
    });
    const doExport = async ()=>{
        setExporting(true);
        try {
            const artifactNames = await resolveNames(exportRows.flatMap((r)=>r.attachments.map((a)=>a.artifactId)));
            const res = await buildOutOfScopeZip(exportRows, {
                teamName: lk.teamName,
                personName: dir.userName,
                artifactName: (id)=>artifactNames.get(id),
                fetchArtifact
            });
            const day = new Date().toISOString().slice(0, 10);
            saveBlob(res.zip, `out-of-scope-${day}.zip`);
            if (res.missing.length) toast.warning(`Exported, but ${res.missing.length} file${res.missing.length === 1 ? '' : 's'} could not be included; the CSV names them.`);
            else toast.success(`Exported ${exportRows.length} ${exportRows.length === 1 ? 'item' : 'items'} and ${res.files} ${res.files === 1 ? 'file' : 'files'}`);
        } catch  {
            toast.error('Could not build the export');
        } finally{
            setExporting(false);
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
        "data-testid": Testids.cmWorkProducts.beyondPage,
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "home",
            title: "Beyond Scope",
            description: "Work your teams did that the award does not pay for. Settle each logged item’s scope, then export the out-of-scope ones for a mod or REA.",
            actions: /*#__PURE__*/ _jsxs(SpecraButton, {
                variant: "secondary",
                disabled: exportRows.length === 0 || exporting,
                onClick: ()=>void doExport(),
                title: exportRows.length ? 'A ZIP with a CSV of the out-of-scope items (who asked, hours, value) and their evidence files' : 'Nothing out of scope in this view yet',
                "data-testid": Testids.cmWorkProducts.export,
                children: [
                    /*#__PURE__*/ _jsx(Download, {
                        className: "h-4 w-4",
                        "aria-hidden": true
                    }),
                    exporting ? 'Building…' : `Export out of scope (${exportRows.length})`
                ]
            })
        }),
        metrics: {
            'data-testid': Testids.cmWorkProducts.totals,
            metrics: [
                {
                    id: 'count',
                    label: 'Logged in view',
                    value: t.count
                },
                {
                    id: 'out',
                    label: 'Out of scope',
                    value: outCount,
                    tone: outCount > 0 ? 'danger' : 'neutral',
                    onClick: ()=>setScope('out')
                },
                {
                    id: 'hours',
                    label: 'Hours',
                    value: formatHours(t.hours),
                    muted: t.hours === 0
                },
                {
                    id: 'value',
                    label: 'Value',
                    value: formatCents(t.valueCents),
                    muted: t.valueCents === 0
                }
            ]
        },
        children: [
            /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                search: false,
                filters: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Filter by scope",
                            size: "sm",
                            className: "w-auto",
                            value: scope,
                            onChange: (e)=>setScope(e.target.value),
                            "data-testid": Testids.cmWorkProducts.filterScope,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Any scope"
                                }),
                                SCOPE_BUCKETS.map((b)=>/*#__PURE__*/ _jsx("option", {
                                        value: b.value,
                                        children: b.label
                                    }, b.value))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Filter by month",
                            size: "sm",
                            className: "w-auto",
                            value: month,
                            onChange: (e)=>setMonth(e.target.value),
                            "data-testid": Testids.cmWorkProducts.filterMonth,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Any month"
                                }),
                                months.map((m)=>/*#__PURE__*/ _jsx("option", {
                                        value: m,
                                        children: monthLabel(m)
                                    }, m))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Filter by team",
                            size: "sm",
                            className: "w-auto",
                            value: team,
                            onChange: (e)=>setTeam(e.target.value),
                            "data-testid": Testids.cmWorkProducts.filterTeam,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Every team"
                                }),
                                lk.workstreams.map((w)=>/*#__PURE__*/ _jsx("option", {
                                        value: w.id,
                                        children: w.name
                                    }, w.id))
                            ]
                        })
                    ]
                }),
                actions: writer.runsDelivery ? /*#__PURE__*/ _jsxs("span", {
                    className: "flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "span",
                            size: "caption",
                            tone: "muted",
                            children: [
                                liveSelected.length,
                                " selected"
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraSelect, {
                            "aria-label": "Scope to set",
                            size: "sm",
                            className: "w-auto",
                            value: bulk,
                            onChange: (e)=>setBulk(e.target.value),
                            "data-testid": Testids.cmWorkProducts.bulkScope,
                            children: SCOPE_BUCKETS.map((b)=>/*#__PURE__*/ _jsx("option", {
                                    value: b.value,
                                    children: b.label
                                }, b.value))
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            disabled: liveSelected.length === 0 || assess.isPending,
                            onClick: ()=>assess.mutate({
                                    workProductIds: liveSelected,
                                    scopeAssessment: SCOPE_BUCKETS.find((b)=>b.value === bulk)?.assessment ?? Scope.UNKNOWN
                                }, {
                                    onSuccess: ()=>setSelected(new Set())
                                }),
                            "data-testid": Testids.cmWorkProducts.bulkApply,
                            children: "Set scope"
                        })
                    ]
                }) : undefined
            }),
            teams.length === 0 && !q.isLoading && !lk.isLoading ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                title: all.length ? 'Nothing matches these filters' : 'No work beyond the award yet',
                description: all.length ? 'Clear a filter to see the rest.' : 'Teams log what they produce on their team page, under Work products. A SOW’s own deliverables appear here too.',
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    onClick: ()=>navigate(`/delivery/programs/${programId}/workstreams`),
                    children: "Open Teams"
                })
            }) : null,
            teams.map(({ ws, sow, logged })=>/*#__PURE__*/ _jsxs("section", {
                    className: "space-y-3",
                    "data-testid": Testids.cmWorkProducts.beyondTeam,
                    "data-row-id": ws.id,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            as: "h2",
                            size: "sm",
                            children: /*#__PURE__*/ _jsx(SpecraLink, {
                                href: `/delivery/programs/${programId}/workstreams/${ws.id}?tab=work`,
                                renderLink: renderRouterLink,
                                children: ws.name
                            })
                        }),
                        sow.length > 0 ? /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            "data-testid": Testids.cmWorkProducts.sowSection,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: `Committed in a SOW, not in the award · ${sow.length}`
                                }),
                                /*#__PURE__*/ _jsx(CmTable, {
                                    caption: `${ws.name}: SOW deliverables the award does not name`,
                                    rows: sow,
                                    rowKey: (d)=>d.id,
                                    rowTestId: Testids.cmWorkProducts.sowRow,
                                    onRowClick: (d)=>navigate(`/delivery/programs/${programId}/deliverables/${d.id}`),
                                    getRowLabel: (d)=>d.title,
                                    columns: [
                                        {
                                            id: 'title',
                                            header: 'Deliverable',
                                            cell: (d)=>/*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium",
                                                    children: /*#__PURE__*/ _jsx(GlossaryText, {
                                                        text: d.title
                                                    })
                                                })
                                        },
                                        {
                                            id: 'pws',
                                            header: 'Source',
                                            cell: (d)=>`SOW${d.pwsRef ? ` PWS ${d.pwsRef}` : ''}`
                                        }
                                    ]
                                })
                            ]
                        }) : null,
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            "data-testid": Testids.cmWorkProducts.loggedSection,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: `Done, not in any document · ${logged.length}`
                                }),
                                /*#__PURE__*/ _jsx(CmTable, {
                                    caption: `${ws.name}: logged work products`,
                                    rows: logged,
                                    columns: columns,
                                    rowKey: (r)=>r.id,
                                    testId: Testids.cmWorkProducts.table,
                                    rowTestId: Testids.cmWorkProducts.row,
                                    getRowLabel: (r)=>r.title,
                                    empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                        size: "compact",
                                        title: "Nothing logged in this view",
                                        description: "The team logs work products on its own page."
                                    })
                                })
                            ]
                        })
                    ]
                }, ws.id))
        ]
    });
}
